import React, { createContext, useContext, useState, useEffect } from 'react';
import { ServerConfigContext } from './ServerConfigContext';

// AuthContext 생성
const AuthContext = createContext();

// 커스텀 훅 for AuthContext 사용
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth는 AuthProvider 내에서만 사용할 수 있습니다.');
  }
  return context;
};

// AuthProvider 컴포넌트
export const AuthProvider = ({ children }) => {
  const { getServer } = useContext(ServerConfigContext) || {};
  const [user, setUser] = useState(null); // 로그인한 사용자 정보
  const [token, setToken] = useState(null); // JWT 토큰
  const [isLoading, setIsLoading] = useState(true); // 초기 로딩 상태
  const [isAuthenticated, setIsAuthenticated] = useState(false); // 인증 상태

  // 로컬 스토리지 키
  const TOKEN_KEY = 'auth_token';
  const USER_KEY = 'user_info';

  // board-api-server URL (프록시 사용)
  const getApiUrl = (endpoint) => {
    return `/board-api/api/v1${endpoint}`;
  };

  // 컴포넌트 마운트 시 저장된 인증 정보 복원
  useEffect(() => {
    const restoreAuthState = async () => {
      try {
        console.log('🔄 [AuthContext] 인증 상태 복원 시작...');
        
        const savedToken = localStorage.getItem(TOKEN_KEY);
        const savedUser = localStorage.getItem(USER_KEY);

        if (savedToken && savedUser) {
          try {
            const parsedUser = JSON.parse(savedUser);
            
            // 토큰 유효성 검사 (프로필 조회로 확인)
            const isValid = await validateToken(savedToken);
            
            if (isValid) {
              setToken(savedToken);
              setUser(parsedUser);
              setIsAuthenticated(true);
              console.log('✅ [AuthContext] 저장된 인증 정보 복원 완료:', parsedUser.nickname);
            } else {
              console.warn('⚠️ [AuthContext] 저장된 토큰이 만료됨, 로그아웃 처리');
              await logout();
            }
          } catch (error) {
            console.error('❌ [AuthContext] 저장된 사용자 정보 파싱 오류:', error);
            await logout();
          }
        } else {
          console.log('ℹ️ [AuthContext] 저장된 인증 정보 없음');
        }
      } catch (error) {
        console.error('❌ [AuthContext] 인증 상태 복원 중 오류:', error);
      } finally {
        setIsLoading(false);
      }
    };

    restoreAuthState();
  }, [getServer]);

  // 토큰 유효성 검사 함수
  const validateToken = async (tokenToValidate) => {
    try {
      const response = await fetch(getApiUrl('/auth/profile'), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokenToValidate}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          // 최신 사용자 정보로 업데이트
          setUser(result.data);
          localStorage.setItem(USER_KEY, JSON.stringify(result.data));
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('❌ [AuthContext] 토큰 유효성 검사 오류:', error);
      return false;
    }
  };

  // 로그인 함수
  const login = async (userId, password) => {
    try {
      console.log('🔐 [AuthContext] 로그인 시도:', userId);
      
      const response = await fetch(getApiUrl('/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          password: password,
        }),
      });

      const result = await response.json();

      if (response.ok && result.success && result.data) {
        const { token: newToken, user: userData } = result.data;
        
        // 상태 업데이트
        setToken(newToken);
        setUser(userData);
        setIsAuthenticated(true);

        // 로컬 스토리지에 저장 (JWT 형식으로도 저장)
        localStorage.setItem(TOKEN_KEY, newToken);
        localStorage.setItem(USER_KEY, JSON.stringify(userData));
        localStorage.setItem('jwtToken', newToken);    // JWT 토큰으로도 저장
        localStorage.setItem('user', JSON.stringify(userData)); // JWT 사용자 정보로도 저장

        // JWTAuthContext에게 변경을 알림 (같은 탭에서)
        const storageChangeEvent = new CustomEvent('localStorageChange', {
          detail: { key: 'jwtToken', newValue: newToken }
        });
        window.dispatchEvent(storageChangeEvent);

        console.log('✅ [AuthContext] 로그인 성공 (JWT 토큰 포함):', userData.nickname);
        return { success: true, user: userData };
      } else {
        console.error('❌ [AuthContext] 로그인 실패:', result.message);
        return { 
          success: false, 
          message: result.message || '로그인에 실패했습니다.' 
        };
      }
    } catch (error) {
      console.error('❌ [AuthContext] 로그인 중 오류:', error);
      return { 
        success: false, 
        message: '로그인 중 오류가 발생했습니다.' 
      };
    }
  };

  // 로그아웃 함수
  const logout = async () => {
    try {
      console.log('🔓 [AuthContext] 로그아웃 처리...');
      
      // 상태 초기화
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);

      // 로컬 스토리지에서 제거 (JWT 토큰도 함께 삭제)
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem('jwtToken'); // JWT 토큰 삭제
      localStorage.removeItem('user');     // JWT 사용자 정보 삭제

      // JWTAuthContext에게 로그아웃을 알림 (같은 탭에서)
      const storageChangeEvent = new CustomEvent('localStorageChange', {
        detail: { key: 'jwtToken', newValue: null }
      });
      window.dispatchEvent(storageChangeEvent);

      console.log('✅ [AuthContext] 로그아웃 완료 (JWT 토큰 포함)');
      return { success: true };
    } catch (error) {
      console.error('❌ [AuthContext] 로그아웃 중 오류:', error);
      return { success: false, message: '로그아웃 중 오류가 발생했습니다.' };
    }
  };

  // 회원가입 함수
  const signup = async (userData) => {
    try {
      console.log('📝 [AuthContext] 회원가입 시도:', userData.user_id);
      
      const response = await fetch(getApiUrl('/auth/signup'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const result = await response.json();

      if (response.ok && result.success && result.data) {
        console.log('✅ [AuthContext] 회원가입 성공:', result.data.nickname);
        return { success: true, user: result.data };
      } else {
        console.error('❌ [AuthContext] 회원가입 실패:', result.message);
        return { 
          success: false, 
          message: result.message || '회원가입에 실패했습니다.' 
        };
      }
    } catch (error) {
      console.error('❌ [AuthContext] 회원가입 중 오류:', error);
      return { 
        success: false, 
        message: '회원가입 중 오류가 발생했습니다.' 
      };
    }
  };

  // 닉네임 중복 체크 함수
  const checkNickname = async (nickname) => {
    try {
      console.log('🔍 [AuthContext] 닉네임 중복 체크:', nickname);
      
      const response = await fetch(getApiUrl('/auth/check-nickname'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ nickname }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        console.log('✅ [AuthContext] 닉네임 체크 완료:', result.data);
        return { 
          success: true, 
          available: result.data.available,
          message: result.data.message 
        };
      } else {
        console.error('❌ [AuthContext] 닉네임 체크 실패:', result.message);
        return { 
          success: false, 
          message: result.message || '닉네임 확인 중 오류가 발생했습니다.' 
        };
      }
    } catch (error) {
      console.error('❌ [AuthContext] 닉네임 체크 중 오류:', error);
      return { 
        success: false, 
        message: '닉네임 확인 중 오류가 발생했습니다.' 
      };
    }
  };

  // 프로필 수정 함수
  const updateProfile = async (updateData) => {
    try {
      console.log('✏️ [AuthContext] 프로필 수정 시도:', updateData);
      
      if (!token) {
        return { success: false, message: '로그인이 필요합니다.' };
      }

      const response = await fetch(getApiUrl('/auth/profile'), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      const result = await response.json();

      if (response.ok && result.success && result.data) {
        // 사용자 정보 업데이트
        setUser(result.data);
        localStorage.setItem(USER_KEY, JSON.stringify(result.data));

        console.log('✅ [AuthContext] 프로필 수정 성공:', result.data.nickname);
        return { success: true, user: result.data };
      } else {
        console.error('❌ [AuthContext] 프로필 수정 실패:', result.message);
        return { 
          success: false, 
          message: result.message || '프로필 수정에 실패했습니다.' 
        };
      }
    } catch (error) {
      console.error('❌ [AuthContext] 프로필 수정 중 오류:', error);
      return { 
        success: false, 
        message: '프로필 수정 중 오류가 발생했습니다.' 
      };
    }
  };

  // 비밀번호 변경 함수
  const changePassword = async (currentPassword, newPassword) => {
    try {
      console.log('🔒 [AuthContext] 비밀번호 변경 시도');
      
      if (!token) {
        return { success: false, message: '로그인이 필요합니다.' };
      }

      const response = await fetch(getApiUrl('/auth/password'), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        console.log('✅ [AuthContext] 비밀번호 변경 성공');
        return { success: true, message: '비밀번호가 성공적으로 변경되었습니다.' };
      } else {
        console.error('❌ [AuthContext] 비밀번호 변경 실패:', result.message);
        return { 
          success: false, 
          message: result.message || '비밀번호 변경에 실패했습니다.' 
        };
      }
    } catch (error) {
      console.error('❌ [AuthContext] 비밀번호 변경 중 오류:', error);
      return { 
        success: false, 
        message: '비밀번호 변경 중 오류가 발생했습니다.' 
      };
    }
  };

  // 회원 탈퇴 함수
  const deleteAccount = async () => {
    try {
      console.log('🗑️ [AuthContext] 회원 탈퇴 시도');
      
      if (!token) {
        return { success: false, message: '로그인이 필요합니다.' };
      }

      const response = await fetch(getApiUrl('/auth/account'), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (response.ok && result.success) {
        console.log('✅ [AuthContext] 회원 탈퇴 성공');
        
        // 로그아웃 처리
        await logout();
        
        return { success: true, message: '회원 탈퇴가 완료되었습니다.' };
      } else {
        console.error('❌ [AuthContext] 회원 탈퇴 실패:', result.message);
        return { 
          success: false, 
          message: result.message || '회원 탈퇴에 실패했습니다.' 
        };
      }
    } catch (error) {
      console.error('❌ [AuthContext] 회원 탈퇴 중 오류:', error);
      return { 
        success: false, 
        message: '회원 탈퇴 중 오류가 발생했습니다.' 
      };
    }
  };

  // 비밀번호 유효성 검사 함수
  const validatePassword = (password) => {
    if (!password || password.length < 12) {
      return { valid: false, message: '비밀번호는 12자리 이상이어야 합니다.' };
    }

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

    if (!hasUpperCase) {
      return { valid: false, message: '비밀번호에 대문자가 포함되어야 합니다.' };
    }
    if (!hasLowerCase) {
      return { valid: false, message: '비밀번호에 소문자가 포함되어야 합니다.' };
    }
    if (!hasNumbers) {
      return { valid: false, message: '비밀번호에 숫자가 포함되어야 합니다.' };
    }
    if (!hasSpecialChar) {
      return { valid: false, message: '비밀번호에 특수문자가 포함되어야 합니다.' };
    }

    return { valid: true, message: '사용 가능한 비밀번호입니다.' };
  };

  // API 요청 헬퍼 함수 (인증 헤더 포함)
  const authenticatedRequest = async (url, options = {}) => {
    if (!token) {
      throw new Error('인증 토큰이 없습니다. 로그인이 필요합니다.');
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    };

    return fetch(url, {
      ...options,
      headers,
    });
  };

  // Context 값
  const contextValue = {
    // 상태 (기존 호환성 유지)
    user,
    currentUser: user, // 기존 코드 호환성
    token,
    isLoading,
    isAuthenticated,
    isLoggedIn: isAuthenticated, // 기존 코드 호환성

    // 인증 관련 함수
    login,
    logout,
    signup,
    checkNickname,
    updateProfile,
    changePassword,
    deleteAccount,

    // 유틸리티 함수
    validatePassword,
    authenticatedRequest,

    // 권한 확인 헬퍼
    isUser: user?.role === 'user',
    isModerator: user?.role === 'moderator',
    isAdmin: user?.role === 'admin',
    hasRole: (role) => user?.role === role, // 기존 코드 호환성
    
    // 사용자 정보 헬퍼
    userNickname: user?.nickname || '',
    userName: user?.nickname || user?.user_id || '', // 기존 코드 호환성
    userId: user?.user_id || '',
    userEmail: user?.email || '',
    userGrade: user?.grade || 'newbie',
    userRole: user?.role || 'guest', // 기존 코드 호환성
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
